import { useContext, useEffect, useState } from "react";
import AuthContext from "../Context/AuthProvider";
import {
  Box,
  CircularProgress,
  FormControl,
  TextField,
  Typography,
  Button,
} from "@mui/material";
import { useSnackbar } from "../Utility/UserMessageContext";
import {
  searchPatients,
  addPatient,
  getPatientDetails,
} from "../API/PatientsApi";
import Autocomplete from "@mui/material/Autocomplete";
import { formatAge } from "../Utility/Utility";
import { Grid2 } from "@mui/material";
import NewPatientDialog from "../Patients/NewPatientDialog";

function SearchSelectPatient1({ setSelectedPatient, disabled }) {
  const { auth } = useContext(AuthContext);
  const { showSnackbar } = useSnackbar();

  const [searchKeyword, setSearchKeyword] = useState("");
  const [searchResult, setSearchResult] = useState([]);
  const [selectedPatientId, setSelectedPatientId] = useState(null);
  const [selectedPatientObject, setSelectedPatientObject] = useState(null); // Used to control Autocomplete selection
  const [isLoading, setIsLoading] = useState(false);
  const [isDialogOpen, setDialogOpen] = useState(false);

  const localStorage_key = "cachedPatients";

  // On mount: load cached patients from localStorage
  useEffect(() => {
    const localStorageData = JSON.parse(
      localStorage.getItem(localStorage_key) || "[]"
    );
    setSearchResult(localStorageData);
  }, []);

  // Save patient to localStorage (with max 50 limit)
  const savePatienceToLocaleStorage = (patient) => {
    const savedItems = JSON.parse(
      localStorage.getItem(localStorage_key) || "[]"
    );
    const exists = savedItems.find((p) => p.id === patient.id);
    if (!exists) {
      const updated = [...savedItems, patient];
      localStorage.setItem(
        localStorage_key,
        JSON.stringify(updated.slice(-50))
      );
    }
  };

  // Search patient locally in cached data
  const searchPatientFromLocalStorage = (keyword) => {
    const data = JSON.parse(localStorage.getItem(localStorage_key) || "[]");
    const term = keyword.toLowerCase().trim();
    return data.filter((p) => {
      const fullName = `${p.prefix || ""} ${p.firstName || ""} ${
        p.lastName || ""
      }`.toLowerCase();
      return (
        fullName.includes(term) ||
        (p.mobileNo || "").toLowerCase().includes(term) ||
        (p.mrn || "").toLowerCase().includes(term) ||
        (p.emailId || "").toLowerCase().includes(term)
      );
    });
  };

  // Handle patient search from API or local
  const searchPatient = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setSearchResult([]);

    if (searchKeyword.trim().length < 3) {
      showSnackbar("Enter at least 3 characters to search.", "error");
      setIsLoading(false);
      return;
    }

    const cached = searchPatientFromLocalStorage(searchKeyword);
    if (cached.length > 0) {
      setSearchResult(cached);
      setIsLoading(false);
      return;
    }

    try {
      const result = await searchPatients(auth, searchKeyword);
      if (result?.isSuccess) {
        setSearchResult(result.data || []);
        result.data.forEach((p) => savePatienceToLocaleStorage(p));
      } else {
        setSearchResult([]);
      }
    } catch (error) {
      console.error("Search error:", error);
      setSearchResult([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle dialog open/close
  const handleOpenDialog = () => setDialogOpen(true);
  const handleCloseDialog = () => setDialogOpen(false);

  // Handle save patient from dialog
  const handleSavePatient = async (patientData) => {
    console.log("Saving patient:", patientData);

    try {
      const results = await addPatient(auth, patientData);

      if (results?.isSuccess) {
        showSnackbar("Patient added successfully!", "success");

        // Get newly created patient full details
        const newPatientdata = await getPatientDetails(auth, results.data);

        if (newPatientdata?.data) {
          const newPatient = newPatientdata.data;

          // Save to local cache
          savePatienceToLocaleStorage(newPatient);
          // ✅ NEW: Update searchResult and immediately select the newly added patient
          // Add to list and set selection
          setSearchResult((prev) => {
            const updated = [...prev, newPatient];

            // ✅ Important: find the matching object from the updated array
            const selected = updated.find((p) => p.id === newPatient.id);

            // ✅ Set selected states with full object
            setSelectedPatientId(selected.id);
            setSelectedPatient(selected);
            setSelectedPatientObject(selected); // 👈 Autocomplete will now show the selection

            return updated;
          });
        }
      } else {
        showSnackbar("Error occurred in adding patient.", "error");
      }
    } catch (err) {
      console.error("Exception during patient save:", err);
      showSnackbar("Unexpected error occurred.", "error");
    } finally {
      setDialogOpen(false);
    }
  };

  return (
    <Grid2 container size={12} spacing={2}>
      <Grid2 size={12}>
        <FormControl fullWidth>
          <form onSubmit={searchPatient}>
            <Autocomplete
              disabled={disabled}
              options={searchResult}
              value={selectedPatientObject}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              noOptionsText="No result found"
              getOptionLabel={(option) => {
                const prefix = option.prefix ? `${option.prefix} ` : "";
                const name = `${option.firstName || ""} ${
                  option.lastName || ""
                }`.trim();
                const details = [
                  option.mrn ? `MRN: ${option.mrn}` : "",
                  option.ageInYears != null
                    ? `Age: ${formatAge(option.ageInYears)}`
                    : "",
                  option.mobileNo ? `Mobile No.: ${option.mobileNo}` : "",
                  option.gender ? `Gender: ${option.gender}` : "",
                ]
                  .filter(Boolean)
                  .join(", ");
                return `${prefix}${name} (${details})`;
              }}
              onInputChange={(e, value) => setSearchKeyword(value)}
              onChange={(e, value) => {
                if (value) {
                  setSelectedPatientId(value.id);
                  setSelectedPatient(value);
                  setSelectedPatientObject(value);
                } else {
                  setSelectedPatientId(null);
                  setSelectedPatient(null);
                  setSelectedPatientObject(null);
                }
              }}
              renderOption={(props, option) => {
                const details = [
                  option.mrn && ` ❖ MRN: ${option.mrn}`,
                  option.ageInYears != null &&
                    `Age: ${formatAge(option.ageInYears)}`,
                  option.mobileNo && `Mobile No.: ${option.mobileNo}`,
                  option.gender && `Gender: ${option.gender}`,
                ]
                  .filter(Boolean)
                  .join(" ❖ "); // Use bullet separator

                return (
                  <li {...props}>
                    <Box display="flex" flexDirection="column" width="100%">
                      <Typography variant="body1" fontWeight={500}>
                        {option.prefix ? `${option.prefix} ` : ""}
                        {option.firstName} {option.lastName}
                      </Typography>
                      {details && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ whiteSpace: "normal" }}
                        >
                          {details}
                        </Typography>
                      )}
                    </Box>
                  </li>
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Search patients"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {isLoading && (
                          <CircularProgress size={20} color="inherit" />
                        )}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </form>
        </FormControl>
      </Grid2>

      {/* Add Patient Button */}
      <Grid2 size={12} item>
        <Box display="flex" justifyContent="flex-end" px={1} py={0.5} m={-1.5}>
          <Button color="primary" size="small" onClick={handleOpenDialog}>
            Add Patient
          </Button>
        </Box>
      </Grid2>

      {/* New Patient Dialog */}
      <NewPatientDialog
        open={isDialogOpen}
        onClose={handleCloseDialog}
        onSave={handleSavePatient}
      />
    </Grid2>
  );
}

export default SearchSelectPatient1;